import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RefreshCcw, Loader2, Zap, Newspaper, CalendarClock } from "lucide-react";
import { formatDateTime } from "@/lib/date";

const CARD_COLS = [
  { key: "detected_today", label: "Detectadas hoy", accent: "text-nightblue" },
  { key: "skipped_old", label: "Descartadas por antiguas", accent: "text-textgray" },
  { key: "skipped_no_date", label: "Sin fecha confirmada", accent: "text-amber-700" },
  { key: "duplicate", label: "Duplicadas", accent: "text-orange-600" },
  { key: "processing", label: "Procesando con IA", accent: "text-indigo-700" },
  { key: "ready_for_review", label: "Pendientes de revisión", accent: "text-purple-700" },
  { key: "approved", label: "Aprobadas", accent: "text-emerald-700" },
  { key: "published", label: "Publicadas", accent: "text-teal-700" },
  { key: "rejected", label: "Rechazadas", accent: "text-red-600" },
  { key: "extraction_error", label: "Errores de extracción", accent: "text-red-600" },
  { key: "ai_error", label: "Errores de IA", accent: "text-red-700" },
];

const PRESETS = [
  { key: "today", label: "Hoy" },
  { key: "yesterday", label: "Ayer" },
  { key: "7d", label: "Últimos 7 días" },
  { key: "30d", label: "Últimos 30 días" },
];

export default function AutomationDashboardTab({ sources = [], onRunAll, runningAll = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState("today");
  const [sourceId, setSourceId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { preset };
      if (sourceId) params.source_id = sourceId;
      const d = await api.adminAutoDashboard(params);
      setData(d);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { preset };
        if (sourceId) params.source_id = sourceId;
        const d = await api.adminAutoDashboard(params);
        if (!cancelled) setData(d);
      } catch (e) { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [preset, sourceId]);

  const s = data?.sources || { total: 0, active: 0, paused: 0 };
  const c = data?.counters || {};
  const latest = data?.latest_run;
  const nextR = data?.next_run;

  return (
    <section>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Fuentes activas" value={s.active} accent="text-emerald-700" icon={Newspaper} />
        <StatCard label="Fuentes pausadas" value={s.paused} accent="text-textgray" icon={Newspaper} />
        <StatCard label="Última ejecución" value={latest ? `${latest.source_name} · ${latest.status}` : "—"} raw />
        <StatCard label="Próxima corrida" value={nextR ? formatDateTime(nextR.at) : "no programada"} raw icon={CalendarClock} />
      </div>

      <div className="flex flex-wrap gap-2 items-end mb-4">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPreset(p.key)}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border transition-colors ${
              preset === p.key ? "bg-nightblue text-white border-nightblue" : "border-softblue text-nightblue hover:bg-lightgray"
            }`}
          >{p.label}</button>
        ))}
        <div className="ml-auto flex gap-2 items-end">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-textgray mb-1">Fuente</div>
            <Select value={sourceId || "all"} onValueChange={(v) => setSourceId(v === "all" ? "" : v)}>
              <SelectTrigger className="min-w-[220px]"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las fuentes</SelectItem>
                {sources.map((src) => <SelectItem key={src.id} value={src.id}>{src.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </Button>
          <Button onClick={onRunAll} disabled={runningAll}>
            {runningAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Buscar noticias ahora
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {CARD_COLS.map((col) => (
          <StatCard key={col.key} label={col.label} value={c[col.key] ?? 0} accent={col.accent} />
        ))}
      </div>

      {/* Latencia por corrida — con vs sin render JS */}
      {data?.latency && (
        <div className="mt-6 bg-white border border-softblue p-4" data-testid="admin-automation-dashboard-latency">
          <div className="text-[10px] uppercase tracking-widest text-textgray font-semibold mb-3">
            Latencia media por corrida
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-textgray">Sin render JS ({data.latency.runs_without_render || 0} corrida{data.latency.runs_without_render === 1 ? "" : "s"})</div>
              <div className="mt-1 font-serif text-2xl font-black text-blueblack">
                {data.latency.without_render != null ? `${(data.latency.without_render / 1000).toFixed(1)} s` : "—"}
              </div>
            </div>
            <div>
              <div className="text-xs text-textgray">Con render JS ({data.latency.runs_with_render || 0} corrida{data.latency.runs_with_render === 1 ? "" : "s"})</div>
              <div className="mt-1 font-serif text-2xl font-black text-blueblack">
                {data.latency.with_render != null ? `${(data.latency.with_render / 1000).toFixed(1)} s` : "—"}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function StatCard({ label, value, accent, icon: Icon, raw }) {
  return (
    <div className="bg-white border border-softblue p-4">
      <div className="flex items-center gap-2 text-textgray">
        {Icon && <Icon className="w-4 h-4" />}
        <span className="text-[10px] uppercase tracking-widest font-semibold">{label}</span>
      </div>
      <div className={`mt-1 ${raw ? "text-sm font-medium text-blueblack" : "font-serif text-3xl font-black text-blueblack"}`}>
        {raw ? value : Number(value || 0).toLocaleString("es-AR")}
      </div>
    </div>
  );
}
